import { Icon, IconName, Text, type SelectItem } from '@talos/kyoko';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUser } from '../../../hooks';
import { useOrganizationsWithVM } from '../../../hooks/useOrganizationsWithVM';
import { useAppReload } from '../../../providers/AppReloadContext';
import { changeOrganization } from '../../../utils/admin';
import { overrideUIChannel } from '../../../utils/channel';
import { MobileOrganizationSelect } from './styles';

export function MobileNavOrganizationSelect() {
  const user = useUser();
  const reloadApp = useAppReload();
  const { organizations: organizationsWithVM } = useOrganizationsWithVM();
  const [selectedOrgID, setSelectedOrgID] = useState<number | undefined>();

  // Initialize the org picker to the active morphed org, or fall back to the user's home org.
  useEffect(() => {
    if (!organizationsWithVM?.length || user == null || selectedOrgID != null) {
      return;
    }

    if (user.UsingOrgID != null) {
      setSelectedOrgID(user.UsingOrgID);
      return;
    }

    const userOrg = organizationsWithVM.find(org => org.Name === user.Organization);
    setSelectedOrgID(userOrg?.ID ?? organizationsWithVM[0].ID);
  }, [organizationsWithVM, selectedOrgID, user]);

  const organizationOptions = useMemo<SelectItem[]>(() => {
    return (
      organizationsWithVM
        ?.toSorted((left, right) => {
          if (left.ID === 1) {
            return -1;
          }
          if (right.ID === 1) {
            return 1;
          }
          return left.Name.localeCompare(right.Name);
        })
        .map(org => ({
          value: org.ID,
          label: `${org.Name} (tal-${org.ID})`,
        })) ?? []
    );
  }, [organizationsWithVM]);

  const selectedOrganizationOption = useMemo(
    () => organizationOptions.find(option => option.value === selectedOrgID),
    [organizationOptions, selectedOrgID]
  );

  const getOrganizationLabel = useCallback((item: { label: string }) => item.label, []);

  const handleChangeOrganization = useCallback(
    async (option: SelectItem<string | number> | undefined) => {
      if (option == null || user == null || organizationsWithVM == null) {
        return;
      }

      const nextOrgID = typeof option.value === 'string' ? Number.parseInt(option.value, 10) : option.value;
      if (nextOrgID === selectedOrgID) {
        return;
      }

      const organization = organizationsWithVM.find(org => org.ID === nextOrgID);
      if (organization == null) {
        return;
      }

      setSelectedOrgID(nextOrgID);
      await changeOrganization(nextOrgID);

      const orgUIChannel = organization.Attrs?.UIChannel ?? user.DefaultUIChannel;
      if (orgUIChannel) {
        overrideUIChannel(orgUIChannel);
      }

      reloadApp();
    },
    [organizationsWithVM, reloadApp, selectedOrgID, user]
  );

  if (selectedOrganizationOption == null || organizationOptions.length === 0) {
    return null;
  }

  return (
    <MobileOrganizationSelect
      prefix={
        <>
          <Icon icon={IconName.OfficeBuilding} />
          <Text color="colorTextDefault" ml="spacing4" mr="spacing8">
            Org
          </Text>
        </>
      }
      selection={selectedOrganizationOption}
      options={organizationOptions}
      getLabel={getOrganizationLabel}
      onChange={handleChangeOrganization}
      dropdownPlacement="top"
      initialSortByLabel={false}
      data-testid="search-select-organization"
    />
  );
}
